// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { toErr } from '@easyflow/shared';
import {
  DndContext, closestCenter, DragEndEvent, DragStartEvent, useSensor, useSensors, PointerSensor
} from '@dnd-kit/core';
import {
  Users, Search, Plus, Edit, Trash2, Eye, Save, ChevronDown, AlertTriangle, CheckCircle,
  Check, TrendingUp, Target, ArrowRightCircle, Code, ToggleLeft, Lock, LayoutGrid, List, Archive
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
import type { NewPipelineEntryDraft } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterClientListRow as MasterClient,
  CrmMasterPipelineStage as PipelineStage,
  CrmMasterPipelineEntry as PipelineEntry,
  CrmMasterLeadFormField as LeadFormField,
  CrmMasterLeadForm as LeadForm,
} from '@easyflow/shared';
import {
  isUserTypingInForm, Tip, pipelineColors, LOCKED_STAGE_SLUGS, InputField, SelectField,
  Badge, ModalWrapper, DroppableColumn, DraggableCard
} from './shared';
import { NewEntryFiscalSection, LeadConversionWizard, PipelineEntryDetail } from './PipelineEntryPanels';

// Task #3875 — lettura difensiva di campi legacy non presenti sul wire attuale.
const legacyStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export function TabPipeline() {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [entries, setEntries] = useState<PipelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const emptyNewEntry = { tenant_id: '', stage_id: '', opportunity_value: '', notes: '', company_name: '', vat_number: '', fiscal_code: '', sdi_code: '', pec: '', legal_address: '', legal_city: '', legal_province: '', legal_zip: '', contact_name: '', contact_email: '', contact_phone: '', lead_source: '', priority: 'medium' };
  const [newEntry, setNewEntry] = useState<NewPipelineEntryDraft>(emptyNewEntry);
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [clients, setClients] = useState<MasterClient[]>([]);
  const [editEntry, setEditEntry] = useState<PipelineEntry | null>(null);
  const [conversionWizardEntry, setConversionWizardEntry] = useState<string | null>(null);
  const [missingDataWarning, setMissingDataWarning] = useState<{ entryId: string; targetStageId: string; missing: string[] } | null>(null);
  const [leadFormsOpen, setLeadFormsOpen] = useState(false);
  const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [editForm, setEditForm] = useState<LeadForm | null>(null);
  const [savingForm, setSavingForm] = useState(false);
  const [pipelineViewMode, setPipelineViewMode] = useState<'kanban' | 'table'>('kanban');
  const [showArchived, setShowArchived] = useState(false);
  const [kanbanSearch, setKanbanSearch] = useState('');
  const [dtSearch, setDtSearch] = useState('');
  const [dtStageFilter, setDtStageFilter] = useState('');
  const [dtPriorityFilter, setDtPriorityFilter] = useState('');
  const [dtDateFrom, setDtDateFrom] = useState('');
  const [dtDateTo, setDtDateTo] = useState('');
  const [dtDateField, setDtDateField] = useState<'created_at' | 'updated_at'>('created_at');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, entriesRes] = await Promise.all([
        api.get<PipelineStage[] | { stages?: PipelineStage[] }>(`${CRM_BASE}/pipeline/stages`),
        api.get<PipelineEntry[] | { entries?: PipelineEntry[] }>(`${CRM_BASE}/pipeline/entries`),
      ]);
      if (stagesRes.success && stagesRes.data) {
        const s = Array.isArray(stagesRes.data) ? stagesRes.data : (stagesRes.data).stages || [];
        setStages(s);
        const first = s[0];
        if (first && !newEntry.stage_id) setNewEntry(e => ({ ...e, stage_id: first.id }));
      }
      if (entriesRes.success && entriesRes.data) {
        setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : (entriesRes.data).entries || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadPipeline(); }, [loadPipeline]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isUserTypingInForm()) return;
      void loadPipeline();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadPipeline]);

  const loadLeadForms = useCallback(async () => {
    setLoadingForms(true);
    try {
      const res = await api.get<LeadForm[] | { forms?: LeadForm[] }>(`${CRM_BASE}/lead-forms`);
      if (res.success && res.data) setLeadForms(Array.isArray(res.data) ? res.data : (res.data).forms || []);
    } catch {} finally { setLoadingForms(false); }
  }, []);

  const saveLeadForm = async () => {
    if (!editForm) return;
    setSavingForm(true);
    try {
      if (editForm.id) {
        await api.put(`${CRM_BASE}/lead-forms/${editForm.id}`, editForm);
      } else {
        await api.post(`${CRM_BASE}/lead-forms`, editForm);
      }
      setEditForm(null);
      void loadLeadForms();
    } catch {} finally { setSavingForm(false); }
  };

  const deleteLeadForm = async (id: string) => {
    if (!confirm('Eliminare questo form?')) return;
    await api.delete(`${CRM_BASE}/lead-forms/${id}`);
    void loadLeadForms();
  };

  const toggleLeadForm = async (form: LeadForm) => {
    // Task #3875: il wire/BE usa `is_active` (colonna lead_forms), non `active`.
    await api.put(`${CRM_BASE}/lead-forms/${form.id}`, { ...form, is_active: !form.is_active });
    void loadLeadForms();
  };

  const copyEmbedCode = (formId: string) => {
    const baseUrl = window.location.origin;
    const snippet = `<div id="edg-form-${formId}"></div>
<script>
(function(){var d=document,f=d.getElementById('edg-form-${formId}');
  fetch('${baseUrl}/api/public/lead-forms/${formId}')
  .then(function(r){return r.json()}).then(function(data){
    if(!data.success)return;var form=data.data,priv=data.privacy,el=d.createElement('form');
    el.style.cssText='max-width:500px;margin:0 auto;font-family:sans-serif;';
    form.fields.forEach(function(field){
      var wrap=d.createElement('div');wrap.style.marginBottom='16px';
      var label=d.createElement('label');label.textContent=field.label;label.style.cssText='display:block;margin-bottom:4px;font-weight:600;';
      wrap.appendChild(label);
      var input;
      if(field.type==='textarea'){input=d.createElement('textarea');input.rows=3;}
      else if(field.type==='select'){input=d.createElement('select');(field.options||'').split(',').forEach(function(o){var opt=d.createElement('option');opt.value=o.trim();opt.textContent=o.trim();input.appendChild(opt);});}
      else{input=d.createElement('input');input.type=field.type||'text';}
      input.name=field.label.toLowerCase().replace(/[^a-z0-9]/g,'_');input.placeholder=field.placeholder||'';
      if(field.required)input.required=true;
      input.style.cssText='width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;box-sizing:border-box;';
      wrap.appendChild(input);el.appendChild(wrap);
    });
    if(priv&&priv.consent_types){
      var gdprSec=d.createElement('div');gdprSec.style.cssText='margin:20px 0;padding:16px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;';
      var gdprTitle=d.createElement('p');gdprTitle.textContent=priv.title||'Informativa Privacy';gdprTitle.style.cssText='font-size:13px;font-weight:600;margin:0 0 12px 0;color:#374151;';
      gdprSec.appendChild(gdprTitle);
      priv.consent_types.forEach(function(ct){
        var cwrap=d.createElement('div');cwrap.style.cssText='display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;';
        var cb=d.createElement('input');cb.type='checkbox';cb.name='gdpr_'+ct.key;cb.style.cssText='margin-top:3px;flex-shrink:0;';
        if(ct.required)cb.required=true;
        var clabel=d.createElement('label');clabel.style.cssText='font-size:12px;color:#4b5563;line-height:1.4;cursor:pointer;';
        clabel.textContent=ct.description||ct.label;if(ct.required){var req=d.createElement('span');req.textContent=' *';req.style.color='#ef4444';clabel.appendChild(req);}
        clabel.onclick=function(){cb.checked=!cb.checked;};
        cwrap.appendChild(cb);cwrap.appendChild(clabel);gdprSec.appendChild(cwrap);
      });
      el.appendChild(gdprSec);
    }
    var btn=d.createElement('button');btn.type='submit';btn.textContent=form.style&&form.style.button_text||'Invia';
    btn.style.cssText='width:100%;padding:12px;background:'+(form.style&&form.style.primary_color||'#3B82F6')+';color:#fff;border:none;border-radius:6px;font-size:16px;cursor:pointer;font-weight:600;';
    el.appendChild(btn);f.appendChild(el);
    el.onsubmit=function(e){e.preventDefault();var fd={};
      new FormData(el).forEach(function(v,k){if(!k.startsWith('gdpr_'))fd[k]=v;});
      var consents=[];
      if(priv&&priv.consent_types){priv.consent_types.forEach(function(ct){
        var cb=el.querySelector('input[name="gdpr_'+ct.key+'"]');
        consents.push({key:ct.key,granted:cb?cb.checked:false});
      });}
      fd.consents=consents;
      fetch('${baseUrl}/api/public/lead-forms/${formId}/submit',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(fd)})
      .then(function(r){return r.json()}).then(function(r){
        if(r.success){el.innerHTML='<div style="text-align:center;padding:20px;color:#22C55E;font-size:18px;font-weight:600;">\\u2713 Inviato con successo!</div>';}
        else{alert(r.error||'Errore');}
      });
    };
  });
}())
</script>`;
    void navigator.clipboard.writeText(snippet);
  };

  const newLeadForm = () => {
    setEditForm({
      name: '', description: '', target_stage_id: stages[0]?.id || '', default_lead_source: 'web_form',
      fields: [{ label: 'Nome', type: 'text', required: true, placeholder: '', options: '' }],
      style: { primary_color: '#3B82F6', button_text: 'Invia' },
      active: true,
      // Task #3875: draft senza id/created_at (form nuovo, non riga wire).
    } as unknown as LeadForm);
  };

  const updateFormField = (idx: number, key: string, value: string | boolean) => {
    setEditForm((f) => {
      const fields = [...((f as LeadForm).fields ?? [])];
      fields[idx] = { ...fields[idx], [key]: value };
      return { ...(f as LeadForm), fields };
    });
  };

  const addFormField = () => {
    setEditForm((f) => ({ ...(f as LeadForm), fields: [...((f as LeadForm).fields ?? []), { label: '', type: 'text', required: false, placeholder: '', options: '' }] }));
  };

  const removeFormField = (idx: number) => {
    setEditForm((f) => ({ ...(f as LeadForm), fields: ((f as LeadForm).fields ?? []).filter((_, i: number) => i !== idx) }));
  };

  const moveFormField = (idx: number, dir: number) => {
    setEditForm((f) => {
      const fields = [...((f as LeadForm).fields ?? [])];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= fields.length) return f;
      [fields[idx], fields[newIdx]] = [fields[newIdx] as LeadFormField, fields[idx] as LeadFormField];
      return { ...(f as LeadForm), fields };
    });
  };

  const loadClients = async () => {
    const res = await api.get<MasterClient[] | { clients?: MasterClient[] }>(`${CRM_BASE}/clients`);
    if (res.success && res.data) setClients(Array.isArray(res.data) ? res.data : (res.data).clients || []);
  };

  const handleDragStart = (event: DragStartEvent) => setActiveId(event.active.id as string);

  const checkMissingFiscalData = (entry: PipelineEntry) => {
    const fields: { key: string; label: string }[] = [
      { key: 'company_name', label: 'Ragione Sociale' },
      { key: 'vat_number', label: 'Partita IVA' },
      { key: 'fiscal_code', label: 'Codice Fiscale' },
      { key: 'sdi_code', label: 'Codice SDI' },
      { key: 'pec', label: 'PEC' },
      { key: 'legal_address', label: 'Indirizzo Legale' },
      { key: 'legal_city', label: 'Città' },
      { key: 'legal_province', label: 'Provincia' },
      { key: 'legal_zip', label: 'CAP' },
    ];
    return fields.filter(f => !(entry as unknown as Record<string, string | undefined>)[f.key]?.trim()).map(f => f.label);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const entryId = active.id as string;
    const targetStageId = over.id as string;
    const entry = entries.find(e => e.id === entryId);
    if (!entry || entry.stage_id === targetStageId) return;
    const targetStage = stages.find(s => s.id === targetStageId);
    const sourceStage = stages.find(s => s.id === entry.stage_id);
    if (!targetStage) return;

    if (LOCKED_STAGE_SLUGS.includes(targetStage.slug ?? '') || LOCKED_STAGE_SLUGS.includes(sourceStage?.slug || '')) {
      return;
    }

    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, stage_id: targetStageId } : e));
    try {
      await api.put(`${CRM_BASE}/pipeline/entries/${entryId}/move`, { to_stage_id: targetStageId });
    } catch {
      void loadPipeline();
    }
  };

  const handleForceMove = async () => {
    if (!missingDataWarning) return;
    const { entryId, targetStageId } = missingDataWarning;
    const entry = entries.find(e => e.id === entryId);
    const stage = stages.find(s => s.id === targetStageId);
    setMissingDataWarning(null);
    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, stage_id: targetStageId } : e));
    try {
      await api.put(`${CRM_BASE}/pipeline/entries/${entryId}/move`, { to_stage_id: targetStageId });
      if (stage?.slug === 'won' && entry && !entry.customer_id) {
        setConversionWizardEntry(entryId);
      }
    } catch {
      void loadPipeline();
    }
  };

  const validateNewEntry = () => {
    const errors: Record<string, string> = {};
    if (!newEntry.company_name?.trim() && !newEntry.contact_name?.trim()) {
      errors.company_name = 'Inserisci almeno Ragione Sociale o Nome Contatto';
      errors.contact_name = 'Inserisci almeno Ragione Sociale o Nome Contatto';
    }
    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddEntry = async () => {
    if (!validateNewEntry()) return;
    const payload = {
      tenant_id: newEntry.tenant_id || null,
      stage_id: newEntry.stage_id || stages[0]?.id || null,
      opportunity_value: parseFloat(String(newEntry.opportunity_value)) || 0,
      notes: newEntry.notes || '',
      company_name: newEntry.company_name || null,
      vat_number: newEntry.vat_number || null,
      fiscal_code: newEntry.fiscal_code || null,
      sdi_code: newEntry.sdi_code || null,
      pec: newEntry.pec || null,
      legal_address: newEntry.legal_address || null,
      legal_city: newEntry.legal_city || null,
      legal_province: newEntry.legal_province || null,
      legal_zip: newEntry.legal_zip || null,
      contact_name: newEntry.contact_name || null,
      contact_email: newEntry.contact_email || null,
      contact_phone: newEntry.contact_phone || null,
      lead_source: newEntry.lead_source || null,
      priority: newEntry.priority || 'medium',
    };
    try {
      const res = await api.post(`${CRM_BASE}/pipeline/entries`, payload);
      if (!res.success) throw new Error(res.error || res.message || 'Errore sconosciuto');
      setShowAddDialog(false);
      setAddErrors({});
      setNewEntry({ ...emptyNewEntry, stage_id: stages[0]?.id || '' });
      void loadPipeline();
    } catch (err: unknown) {
      const __err = toErr(err);
      console.error('[Pipeline] Errore creazione lead:', err, payload);
      setAddErrors({ _general: __err?.message || 'Errore nella creazione della lead' });
    }
  };

  const handleUpdateEntry = async () => {
    if (!editEntry) return;
    try {
      await api.put(`${CRM_BASE}/pipeline/entries/${editEntry.id}`, {
        opportunity_value: parseFloat(String(editEntry.opportunity_value)) || 0,
        notes: editEntry.notes,
        loss_reason: editEntry.loss_reason,
        assigned_to: editEntry.assigned_to,
        contact_name: editEntry.contact_name,
        contact_email: editEntry.contact_email,
        contact_phone: editEntry.contact_phone,
        lead_source: editEntry.lead_source,
        priority: editEntry.priority,
        tags: editEntry.tags,
        next_followup_at: editEntry.next_followup_at,
        company_name: editEntry.company_name,
        vat_number: editEntry.vat_number,
        fiscal_code: editEntry.fiscal_code,
        sdi_code: editEntry.sdi_code,
        pec: editEntry.pec,
        legal_address: editEntry.legal_address,
        legal_city: editEntry.legal_city,
        legal_province: editEntry.legal_province,
        legal_zip: editEntry.legal_zip,
      });
      setEditEntry(null);
      void loadPipeline();
    } catch {}
  };

  const handleDeleteEntry = async (entryId?: string) => {
    const targetId = entryId || editEntry?.id;
    if (!targetId) return;
    if (!confirm('Sei sicuro di voler eliminare questo lead?')) return;
    try {
      const res = await api.delete(`${CRM_BASE}/pipeline/entries/${targetId}`);
      if (!res.success) throw new Error(res.error || 'Errore eliminazione');
      setEditEntry(null);
      void loadPipeline();
    } catch (err: unknown) {
      const __err = toErr(err);
      alert(__err?.message || 'Errore nell\'eliminazione del lead');
    }
  };

  const totalValue = entries.reduce((sum, e) => sum + (parseFloat(String(e.opportunity_value)) || 0), 0);
  const wonStage = stages.find(s => s.slug === 'won');
  const wonValue = wonStage ? entries.filter(e => e.stage_id === wonStage.id).reduce((sum, e) => sum + (parseFloat(String(e.opportunity_value)) || 0), 0) : 0;

  if (loading) return <div className="text-muted-foreground dark:text-muted-foreground/70 p-8 text-center">Caricamento pipeline...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Pipeline</p>
              <p className="text-sm font-bold text-foreground dark:text-white">{entries.length} opportunità</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Valore totale</p>
              <p className="text-sm font-bold text-foreground dark:text-white">€ {totalValue.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          {wonValue > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Vinti</p>
                <p className="text-sm font-bold text-green-600 dark:text-green-400">€ {wonValue.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-border dark:border-gray-600 overflow-hidden">
            <button onClick={() => setPipelineViewMode('kanban')}
              className={`px-3 py-2 text-sm ${pipelineViewMode === 'kanban' ? 'bg-blue-600 text-white' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button onClick={() => setPipelineViewMode('table')}
              className={`px-3 py-2 text-sm ${pipelineViewMode === 'table' ? 'bg-blue-600 text-white' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
              <List className="h-4 w-4" />
            </button>
          </div>
          {pipelineViewMode === 'kanban' && (
            <>
              <div className="relative min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
                <input value={kanbanSearch} onChange={(e) => setKanbanSearch(e.target.value)} placeholder="Cerca lead..."
                  className="w-full pl-8 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground/70 cursor-pointer">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded" />
                <Archive className="h-3.5 w-3.5" />
                Archiviati
              </label>
            </>
          )}
          <button onClick={() => { setShowAddDialog(true); void loadClients(); }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md">
            <Plus className="h-4 w-4" /> Nuova Opportunità
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
        <button onClick={() => { setLeadFormsOpen(!leadFormsOpen); if (!leadFormsOpen) void loadLeadForms(); }}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 dark:hover:bg-gray-700/50 transition-colors">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-purple-500" />
            <span className="text-foreground dark:text-white font-semibold text-sm">Form Lead</span>
            {leadForms.length > 0 && <Badge className="bg-purple-500/20 text-purple-400">{leadForms.length}</Badge>}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground/70 transition-transform ${leadFormsOpen ? 'rotate-180' : ''}`} />
        </button>
        {leadFormsOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-border dark:border-gray-700 pt-3">
            <div className="flex justify-end">
              <button onClick={newLeadForm}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
                <Plus className="h-4 w-4" /> Nuovo Form
              </button>
            </div>
            {loadingForms ? <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Caricamento...</p> : leadForms.length === 0 ? (
              <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm text-center py-4">Nessun form creato</p>
            ) : (
              <div className="space-y-2">
                {leadForms.map((form) => (
                  <div key={form.id} className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground dark:text-white font-medium text-sm">{form.name}</span>
                        <Badge className={form.is_active ? 'bg-green-500/20 text-green-400' : 'bg-muted/500/20 text-muted-foreground/70'}>{form.is_active ? 'Attivo' : 'Inattivo'}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-0.5">
                        {form.fields?.length || 0} campi · {form.submissions_count || 0} invii
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tip label="Attiva/Disattiva"><button onClick={() => toggleLeadForm(form)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><ToggleLeft className="h-4 w-4" /></button></Tip>
                      <Tip label="Modifica"><button onClick={() => setEditForm({ ...form })} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Edit className="h-4 w-4" /></button></Tip>
                      <Tip label="Copia Embed Code"><button onClick={() => copyEmbedCode(form.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-purple-400 p-1"><Code className="h-4 w-4" /></button></Tip>
                      <Tip label="Elimina"><button onClick={() => deleteLeadForm(form.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ModalWrapper open={!!editForm} onClose={() => setEditForm(null)} title={editForm?.id ? 'Modifica Form Lead' : 'Nuovo Form Lead'} wide>
        {editForm && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Nome" value={editForm.name} onChange={(v: string) => setEditForm((f) => ({ ...(f as LeadForm), name: v }))} />
              <InputField label="Descrizione" value={editForm.description || ''} onChange={(v: string) => setEditForm((f) => ({ ...(f as LeadForm), description: v }))} />
              <SelectField label="Fase Target" value={editForm.target_stage_id || ''} onChange={(v: string) => setEditForm((f) => ({ ...(f as LeadForm), target_stage_id: v }))}
                options={stages.map(s => ({ value: s.id, label: s.name }))} />
              <SelectField label="Fonte Lead Default" value={editForm.default_lead_source || ''} onChange={(v: string) => setEditForm((f) => ({ ...(f as LeadForm), default_lead_source: v }))}
                options={[{ value: 'web_form', label: 'Web Form' }, { value: 'phone', label: 'Telefono' }, { value: 'referral', label: 'Referral' }, { value: 'email', label: 'Email' }, { value: 'social', label: 'Social' }, { value: 'event', label: 'Evento' }, { value: 'other', label: 'Altro' }]} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground dark:text-white">Campi</label>
                <button onClick={addFormField} className="flex items-center gap-1 text-blue-500 hover:text-blue-400 text-sm font-medium">
                  <Plus className="h-3.5 w-3.5" /> Aggiungi Campo
                </button>
              </div>
              <div className="space-y-2">
                {(editForm.fields || []).map((field, idx: number) => (
                  <div key={idx} className="bg-muted/50 dark:bg-gray-700/30 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveFormField(idx, -1)} disabled={idx === 0} className="text-muted-foreground/70 hover:text-muted-foreground disabled:opacity-30"><ChevronDown className="h-3 w-3 rotate-180" /></button>
                        <button onClick={() => moveFormField(idx, 1)} disabled={idx === (editForm.fields?.length ?? 0) - 1} className="text-muted-foreground/70 hover:text-muted-foreground disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
                      </div>
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <InputField label="Label" value={field.label} onChange={(v: string) => updateFormField(idx, 'label', v)} />
                        <SelectField label="Tipo" value={field.type} onChange={(v: string) => updateFormField(idx, 'type', v)}
                          options={[{ value: 'text', label: 'Testo' }, { value: 'email', label: 'Email' }, { value: 'tel', label: 'Telefono' }, { value: 'textarea', label: 'Textarea' }, { value: 'select', label: 'Select' }]} />
                        <InputField label="Placeholder" value={field.placeholder || ''} onChange={(v: string) => updateFormField(idx, 'placeholder', v)} />
                        {field.type === 'select' && (
                          <InputField label="Opzioni (virgola)" value={field.options || ''} onChange={(v: string) => updateFormField(idx, 'options', v)} />
                        )}
                      </div>
                      <label className="flex items-center gap-1 text-xs text-muted-foreground dark:text-muted-foreground/70 whitespace-nowrap">
                        <input type="checkbox" checked={field.required || false} onChange={(e) => updateFormField(idx, 'required', e.target.checked)}
                          className="rounded border-border dark:border-gray-600" />
                        Obbligatorio
                      </label>
                      <button onClick={() => removeFormField(idx)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">Stile</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Colore Primario</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editForm.style?.primary_color || '#3B82F6'}
                      onChange={(e) => setEditForm((f) => ({ ...(f as LeadForm), style: { ...(f as LeadForm).style, primary_color: e.target.value } }))}
                      className="w-10 h-10 rounded-lg border border-border dark:border-gray-600 cursor-pointer" />
                    <span className="text-sm text-muted-foreground dark:text-muted-foreground/70 font-mono">{editForm.style?.primary_color || '#3B82F6'}</span>
                  </div>
                </div>
                <InputField label="Testo Pulsante" value={editForm.style?.button_text || 'Invia'} onChange={(v: string) => setEditForm((f) => ({ ...(f as LeadForm), style: { ...(f as LeadForm).style, button_text: v } }))} />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-border dark:border-gray-700">
              <button onClick={() => setEditForm(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={saveLeadForm} disabled={savingForm}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
                <Save className="h-4 w-4" />{savingForm ? 'Salvataggio...' : 'Salva Form'}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>

      {pipelineViewMode === 'kanban' && (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '380px' }}>
          {stages.filter(s => s.slug !== 'archived' || showArchived).map((stage) => {
            const stageEntries = entries.filter(e => {
              if (e.stage_id !== stage.id) return false;
              if (kanbanSearch) {
                const q = kanbanSearch.toLowerCase();
                if (!(e.company_name || '').toLowerCase().includes(q) &&
                    !(e.contact_name || '').toLowerCase().includes(q) &&
                    !(e.contact_email || '').toLowerCase().includes(q) &&
                    !(e.contact_phone || '').toLowerCase().includes(q) &&
                    !(e.vat_number || '').toLowerCase().includes(q)) return false;
              }
              return true;
            });
            const color = stage.color || '#6B7280';
            const stageValue = stageEntries.reduce((sum: number, e: PipelineEntry) => sum + (parseFloat(String(e.opportunity_value)) || 0), 0);
            const isLocked = LOCKED_STAGE_SLUGS.includes(stage.slug ?? '');
            const isArchived = stage.slug === 'archived';
            return (
              <div key={stage.id}
                className={`bg-gradient-to-b from-white/80 to-gray-50/50 dark:from-gray-800/80 dark:to-gray-900/50 backdrop-blur-sm rounded-2xl p-3.5 min-h-[380px] border border-border/60 dark:border-gray-700/40 shadow-[0_4px_20px_rgba(0,0,0,0.04)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.2)] ${isArchived ? 'opacity-60' : ''}`}
                style={{ flex: '1 0 260px', minWidth: '260px' }}
              >
                <div
                  className="flex items-center gap-2.5 mb-4 p-3 rounded-xl backdrop-blur-sm shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${color}15, ${color}08)`,
                    borderLeft: `4px solid ${color}`,
                    borderTop: `1px solid ${color}20`,
                    borderRight: `1px solid ${color}10`,
                    borderBottom: `1px solid ${color}10`
                  }}
                >
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color, boxShadow: `0 2px 8px ${color}50` }} />
                  <span className="text-sm font-bold text-foreground dark:text-foreground truncate flex-1">{stage.name}</span>
                  {isLocked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted/500/20 text-muted-foreground/70" title="Gestito automaticamente dal datatable preventivi">
                      <Lock className="h-3 w-3" /> Auto
                    </span>
                  )}
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full shadow-sm"
                    style={{
                      background: `linear-gradient(135deg, ${color}30, ${color}15)`,
                      color: color,
                      border: `1px solid ${color}25`
                    }}
                  >
                    {stageEntries.length}
                  </span>
                </div>
                {stageValue > 0 && (
                  <div className="mb-3 px-1">
                    <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground">€ {stageValue.toLocaleString('it-IT', { minimumFractionDigits: 0 })}</p>
                  </div>
                )}
                <DroppableColumn id={stage.id}>
                  {stageEntries.map((entry) => (
                    <DraggableCard key={entry.id} entry={entry} stageColor={color} onClick={() => setEditEntry({ ...entry, stage_slug: stage.slug })} />
                  ))}
                  {stageEntries.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground/70 dark:text-muted-foreground text-sm border-2 border-dashed border-border/60 dark:border-gray-700/40 rounded-xl bg-gradient-to-b from-white/30 dark:from-gray-800/30 to-transparent">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/60 dark:bg-gray-700/40 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-muted-foreground/60 dark:text-muted-foreground" />
                      </div>
                      <span className="text-xs">Trascina qui</span>
                    </div>
                  )}
                </DroppableColumn>
              </div>
            );
          })}
        </div>
      </DndContext>
      )}

      {pipelineViewMode === 'table' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
              <input value={dtSearch} onChange={(e) => setDtSearch(e.target.value)} placeholder="Cerca per nome, email, telefono, P.IVA, ragione sociale..."
                className="w-full pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
            </div>
            <select value={dtStageFilter} onChange={(e) => setDtStageFilter(e.target.value)}
              className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Tutti gli stage</option>
              {stages.map(s => <option key={s.id} value={s.slug ?? ''}>{s.name}</option>)}
            </select>
            <select value={dtPriorityFilter} onChange={(e) => setDtPriorityFilter(e.target.value)}
              className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Tutte le priorità</option>
              <option value="low">Bassa</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground/70 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded" />
              <Archive className="h-3.5 w-3.5" />
              Archiviati
            </label>
            <select value={dtDateField} onChange={(e) => setDtDateField(e.target.value as 'created_at' | 'updated_at')}
              className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-2 text-sm">
              <option value="created_at">Creazione</option>
              <option value="updated_at">Aggiornamento</option>
            </select>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground/70">Da:</span>
              <input type="date" value={dtDateFrom} onChange={(e) => setDtDateFrom(e.target.value)}
                className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-2 text-sm" />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground/70">A:</span>
              <input type="date" value={dtDateTo} onChange={(e) => setDtDateTo(e.target.value)}
                className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-2 text-sm" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Ragione Sociale</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Contatto</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Telefono</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">P.IVA</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Stage</th>
                    <th className="text-right py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Valore</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Fonte</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Priorità</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Creato</th>
                    <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Aggiornato</th>
                    <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Prev.</th>
                    <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.filter(e => {
                    const stg = stages.find(s => s.id === e.stage_id);
                    if (stg?.slug === 'archived' && dtStageFilter !== 'archived' && !showArchived) return false;
                    if (dtStageFilter && stg?.slug !== dtStageFilter) return false;
                    if (dtPriorityFilter && e.priority !== dtPriorityFilter) return false;
                    const dateVal = e[dtDateField];
                    if (dtDateFrom && dateVal && new Date(dateVal) < new Date(dtDateFrom)) return false;
                    if (dtDateTo && dateVal && new Date(dateVal) > new Date(dtDateTo + 'T23:59:59')) return false;
                    if (dtSearch) {
                      const q = dtSearch.toLowerCase();
                      if (!(e.company_name || '').toLowerCase().includes(q) &&
                          !(e.contact_name || '').toLowerCase().includes(q) &&
                          !(e.contact_email || '').toLowerCase().includes(q) &&
                          !(e.contact_phone || '').toLowerCase().includes(q) &&
                          !(e.vat_number || '').toLowerCase().includes(q)) return false;
                    }
                    return true;
                  }).map(entry => {
                    const stg = stages.find(s => s.id === entry.stage_id);
                    const isOpen = !['won', 'lost', 'archived'].includes(stg?.slug || '');
                    const priorityBadge: Record<string, string> = { low: 'bg-muted/500/20 text-muted-foreground/70', medium: 'bg-blue-500/20 text-blue-400', high: 'bg-orange-500/20 text-orange-400', urgent: 'bg-red-500/20 text-red-400' };
                    return (
                      <tr key={entry.id} className="border-b border-border dark:border-gray-700/50 hover:bg-muted dark:hover:bg-gray-700/50 transition-colors">
                        <td className="py-3 px-4 text-foreground dark:text-white text-sm font-medium">{entry.company_name || '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm">{entry.contact_name || '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm">{entry.contact_email || '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm">{entry.contact_phone || '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm font-mono text-xs">{entry.vat_number || '-'}</td>
                        <td className="py-3 px-4"><Badge className={pipelineColors[stg?.slug || ''] || ''}>{stg?.name || '-'}</Badge></td>
                        <td className="py-3 px-4 text-right text-muted-foreground dark:text-muted-foreground/60 text-sm">€ {Number(entry.opportunity_value || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs">{entry.lead_source || '-'}</td>
                        <td className="py-3 px-4"><Badge className={priorityBadge[entry.priority ?? ''] || ''}>{entry.priority || '-'}</Badge></td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs">{entry.created_at ? new Date(entry.created_at).toLocaleDateString('it-IT') : '-'}</td>
                        <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs">{entry.updated_at ? new Date(entry.updated_at).toLocaleDateString('it-IT') : '-'}</td>
                        <td className="py-3 px-4 text-center text-muted-foreground dark:text-muted-foreground/70 text-sm">{entry.quotes_count || 0}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <Tip label="Dettaglio"><button onClick={() => setEditEntry({ ...entry, stage_slug: stg?.slug })} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-blue-400 p-1"><Eye className="h-4 w-4" /></button></Tip>
                            {isOpen && (
                              <Tip label="Elimina"><button onClick={() => handleDeleteEntry(entry.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
                            )}
                            {stg?.slug === 'won' && !entry.customer_id && (
                              <Tip label="Converti in Cliente"><button onClick={() => setConversionWizardEntry(entry.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-green-400 p-1"><ArrowRightCircle className="h-4 w-4" /></button></Tip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ModalWrapper open={showAddDialog} onClose={() => { setShowAddDialog(false); setAddErrors({}); }} title="Aggiungi alla Pipeline" wide>
        <div className="space-y-4">
          {Object.keys(addErrors).length > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{addErrors._general || 'Compila i campi obbligatori evidenziati'}</span>
            </div>
          )}

          <div className="bg-muted/50 dark:bg-gray-700/30 rounded-xl border border-border dark:border-gray-600/50 p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Identificazione</p>
            <InputField label="Ragione Sociale" value={newEntry.company_name} onChange={(v: string) => { setNewEntry(e => ({ ...e, company_name: v })); setAddErrors(er => { const n = { ...er }; delete n.company_name; if (v.trim()) delete n.contact_name; return n; }); }} error={addErrors.company_name} required uppercase />
            <InputField label="Nome Contatto" value={newEntry.contact_name} onChange={(v: string) => { setNewEntry(e => ({ ...e, contact_name: v })); setAddErrors(er => { const n = { ...er }; delete n.contact_name; if (v.trim()) delete n.company_name; return n; }); }} error={addErrors.contact_name} required placeholder="Nome e cognome referente" uppercase />
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Email Contatto" value={newEntry.contact_email} onChange={(v: string) => setNewEntry(e => ({ ...e, contact_email: v }))} type="email" placeholder="email@esempio.it" />
              <InputField label="Telefono" value={newEntry.contact_phone} onChange={(v: string) => setNewEntry(e => ({ ...e, contact_phone: v }))} placeholder="+39 ..." />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Tenant Esistente" value={newEntry.tenant_id} onChange={(v: string) => setNewEntry(e => ({ ...e, tenant_id: v }))}
              options={[{ value: '', label: 'Nessuno (nuovo lead)' }, ...clients.map(c => ({ value: c.id, label: c.company_name || c.contact_name || legacyStr(c.tenant_name) || 'N/A' }))]} />
            <SelectField label="Fase Iniziale" value={newEntry.stage_id} onChange={(v: string) => setNewEntry(e => ({ ...e, stage_id: v }))}
              options={stages.map(s => ({ value: s.id, label: s.name }))} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <InputField label="Valore Opportunità (€)" value={newEntry.opportunity_value} onChange={(v: string) => setNewEntry(e => ({ ...e, opportunity_value: v }))} type="number" />
            <SelectField label="Fonte Lead" value={newEntry.lead_source} onChange={(v: string) => setNewEntry(e => ({ ...e, lead_source: v }))}
              options={[{ value: '', label: 'Non specificata' }, { value: 'website', label: 'Sito Web' }, { value: 'referral', label: 'Referral' }, { value: 'cold_call', label: 'Cold Call' }, { value: 'social', label: 'Social Media' }, { value: 'partner', label: 'Partner' }, { value: 'event', label: 'Evento' }, { value: 'other', label: 'Altro' }]} />
            <SelectField label="Priorità" value={newEntry.priority} onChange={(v: string) => setNewEntry(e => ({ ...e, priority: v }))}
              options={[{ value: 'low', label: 'Bassa' }, { value: 'medium', label: 'Media' }, { value: 'high', label: 'Alta' }, { value: 'urgent', label: 'Urgente' }]} />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Note</label>
            <textarea value={newEntry.notes ?? ''} onChange={(e) => setNewEntry(en => ({ ...en, notes: e.target.value }))}
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-20 resize-none" placeholder="Appunti iniziali sulla lead..." />
          </div>
          <NewEntryFiscalSection entry={newEntry} setEntry={setNewEntry} />
          <div className="flex justify-end gap-3">
            <button onClick={() => { setShowAddDialog(false); setAddErrors({}); }} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={handleAddEntry} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Aggiungi Lead</button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper open={!!missingDataWarning} onClose={() => setMissingDataWarning(null)} title="Dati Fiscali Incompleti">
        {missingDataWarning && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-300 mb-1">Per spostare in "Vinto" servono i dati fiscali completi</p>
                <p className="text-xs text-amber-400/70 mb-2">Questi campi sono necessari per la conversione in contratto/tenant:</p>
                <div className="flex flex-wrap gap-1.5">
                  {missingDataWarning.missing.map(m => (
                    <span key={m} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400 border border-red-500/30">{m}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setMissingDataWarning(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={() => {
                const entry = entries.find(e => e.id === missingDataWarning.entryId);
                setMissingDataWarning(null);
                if (entry) setEditEntry({ ...entry });
              }} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                <Edit className="h-4 w-4" /> Compila Dati
              </button>
              <button onClick={handleForceMove} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Sposta Comunque
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!editEntry} onClose={() => setEditEntry(null)} title="Dettaglio Entry Pipeline" wide>
        {editEntry && (
          <PipelineEntryDetail entry={editEntry} setEditEntry={setEditEntry} onSave={handleUpdateEntry} onClose={() => setEditEntry(null)}
            onConvert={() => { setConversionWizardEntry(editEntry.id); setEditEntry(null); }}
            onDelete={handleDeleteEntry} />
        )}
      </ModalWrapper>

      <LeadConversionWizard
        open={!!conversionWizardEntry}
        onClose={() => setConversionWizardEntry(null)}
        entryId={conversionWizardEntry || ''}
        onConverted={() => { setConversionWizardEntry(null); void loadPipeline(); }}
      />
    </div>
  );
}

